import { Link } from 'react-router-dom';
import { useWaitlist } from './MarketingLayout';
import { useMarketingMeta } from './useMarketingMeta';

export default function VolunteersPage() {
  const { openWaitlist } = useWaitlist();

  useMarketingMeta({
    title: 'VolunteerCo · For Volunteers',
    description:
      'VolunteerCo helps you join ongoing volunteer roles with clear expectations and hours that get signed off by the club. Australia-wide.',
  });

  return (
    <>
      <section className="oct16-page-hero" aria-labelledby="volunteers-page-title">
        <div className="oct16-container oct16-page-hero-grid">
          <div className="oct16-page-hero-inner">
            <p className="oct16-eyebrow" data-reveal>
              For Volunteers
            </p>
            <h1 id="volunteers-page-title" className="oct16-page-title" data-reveal>
              Clear roles.
              <span className="oct16-blue-inline"> Real impact.</span>
            </h1>
            <p className="oct16-body" data-reveal>
              VolunteerCo is building a clearer way to join ongoing roles at clubs and community groups across Australia.
            </p>
            <div className="oct16-hero-actions" data-reveal>
              <button type="button" className="oct16-primary" onClick={() => openWaitlist({ persona: 'volunteer' })}>
                Join the waitlist
              </button>
              <Link className="oct16-secondary" to={{ pathname: '/', hash: '#impact' }}>
                See how it works
              </Link>
            </div>
          </div>

          <div className="oct16-page-hero-media" aria-hidden="true">
            <picture className="oct16-photo oct16-photo-wide">
              <source srcSet="/landing/oct16/volunteers-page-800.webp 800w, /landing/oct16/volunteers-page-1600.webp 1600w, /landing/oct16/volunteers-page-2400.webp 2400w" />
              <img
                src="/landing/oct16/volunteers-page-1600.webp"
                alt=""
                loading="lazy"
                decoding="async"
                sizes="(max-width: 960px) 92vw, 44vw"
              />
            </picture>
          </div>
        </div>
      </section>

      <section className="oct16-page-section" aria-labelledby="volunteers-how-title">
        <div className="oct16-container">
          <p className="oct16-label" data-reveal>
            How it works
          </p>
          <h2 id="volunteers-how-title" className="oct16-section-title" data-reveal>
            What to expect
          </h2>

          <div className="oct16-steps" role="list" aria-label="How VolunteerCo works for volunteers">
            <article className="oct16-step" role="listitem" data-reveal>
              <div className="oct16-step-num" aria-hidden="true">
                01
              </div>
              <div className="oct16-step-copy">
                <h3>Pick a role you can actually keep.</h3>
                <p>Roles are designed to be ongoing and repeatable, not one-off chaos.</p>
              </div>
            </article>
            <article className="oct16-step" role="listitem" data-reveal>
              <div className="oct16-step-num" aria-hidden="true">
                02
              </div>
              <div className="oct16-step-copy">
                <h3>Know what you’re saying yes to.</h3>
                <p>Clear expectations: when it’s on, what you’ll do, and how long it usually takes.</p>
              </div>
            </article>
            <article className="oct16-step" role="listitem" data-reveal>
              <div className="oct16-step-num" aria-hidden="true">
                03
              </div>
              <div className="oct16-step-copy">
                <h3>Hours that get recognised.</h3>
                <p>Your weekly hours can be tracked and signed off by the club.</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="oct16-page-section" aria-labelledby="volunteers-examples-title">
        <div className="oct16-container">
          <p className="oct16-label" data-reveal>
            Example roles
          </p>
          <h2 id="volunteers-examples-title" className="oct16-section-title" data-reveal>
            What it could look like
          </h2>
          <p className="oct16-body" data-reveal>
            These are example role cards (not live listings yet).
          </p>

          <div className="oct16-examples-grid" role="list" aria-label="Example roles">
            {[
              { role: 'Canteen helper', club: 'Local surf club', when: 'Sat mornings', time: '3 hrs a week' },
              { role: 'Junior training assistant', club: 'Community footy club', when: 'Weeknights', time: '1 to 2 hrs' },
              { role: 'Grounds and gear', club: 'Local cricket club', when: 'Weekends', time: '2 hrs' },
            ].map((item) => (
              <article className="oct16-role-card oct16-role-card--example" role="listitem" key={item.role} data-reveal>
                <div className="oct16-role-top">
                  <div>
                    <div className="oct16-role-title">{item.role}</div>
                    <div className="oct16-role-club">{item.club}</div>
                  </div>
                  <span className="oct16-role-badge">Example</span>
                </div>
                <div className="oct16-role-meta">
                  <div>
                    <div className="oct16-role-meta-k">When</div>
                    <div className="oct16-role-meta-v">{item.when}</div>
                  </div>
                  <div>
                    <div className="oct16-role-meta-k">Time</div>
                    <div className="oct16-role-meta-v">{item.time}</div>
                  </div>
                </div>
                <button type="button" className="oct16-role-cta" onClick={() => openWaitlist({ persona: 'volunteer' })}>
                  Express interest
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="oct16-page-section" aria-labelledby="volunteers-faq-title">
        <div className="oct16-container">
          <p className="oct16-label" data-reveal>
            FAQs
          </p>
          <h2 id="volunteers-faq-title" className="oct16-section-title" data-reveal>
            A few quick answers
          </h2>
          <div className="oct16-faq" data-reveal>
            <details>
              <summary>Is this a one-off volunteer board?</summary>
              <p>No. VolunteerCo is focused on ongoing roles that clubs can rely on week to week.</p>
            </details>
            <details>
              <summary>Do I need experience?</summary>
              <p>No. Roles are designed to be learnable and supported by the club’s normal routine.</p>
            </details>
          </div>
          <div className="oct16-page-cta" data-reveal>
            <button type="button" className="oct16-primary" onClick={() => openWaitlist({ persona: 'volunteer' })}>
              Join the waitlist
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

